import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import {
  ROTULO_RAZAO,
  dataHoraBr,
  decidir,
  lerAuditoria,
  lerConfig,
  lerIntegracoes,
  lerPainel,
  lerRevisoes,
  salvarConfig,
  sincronizarAgora,
} from '../../encomendas-fluxo';
import type { ConfigDasEncomendas, Integracoes, Painel, Revisao } from '../../encomendas-fluxo';
import type { FiltroDaLista } from './ListaDeEncomendas';
import { ConfiguracoesDasFaltas } from './Faltas';

/**
 * ENCOMENDAS · ADMIN (fases 9 e 10): painel com "Precisa da sua atenção", fila de vínculos para
 * revisar, configurações (SLA, consulta, regras, distribuidoras, integrações) e auditoria.
 */

/* ------------------------------------------------------------------ *
 * Painel
 * ------------------------------------------------------------------ */

const CARTOES: readonly [keyof Painel['indicadores'], string][] = [
  ['hoje', 'Encomendas hoje'],
  ['abertas', 'Abertas'],
  ['aguardandoPedido', 'Aguardando pedido'],
  ['compradas', 'Compradas'],
  ['aCaminho', 'A caminho'],
  ['chegaram', 'Chegaram'],
  ['prontas', 'Prontas p/ cliente'],
  ['atrasadas', 'Atrasadas'],
  ['comProblema', 'Com problema'],
  ['resolvidasHoje', 'Resolvidas hoje'],
];

export function PainelDeEncomendas({ aoFiltrar }: { aoFiltrar: (f: FiltroDaLista) => void }) {
  const [p, setP] = useState<Painel | null>(null);
  useEffect(() => {
    let vivo = true;
    const ler = () => void lerPainel().then((r) => vivo && r.ok && setP(r.dados));
    ler();
    const t = window.setInterval(ler, 20_000);
    return () => {
      vivo = false;
      window.clearInterval(t);
    };
  }, []);
  if (!p)
    return (
      <div className="enc-kpis enc-esqueleto">
        {CARTOES.map(([k]) => (
          <div key={k} className="enc-kpi">
            <i />
          </div>
        ))}
      </div>
    );
  return (
    <>
      <div className="enc-kpis">
        {CARTOES.map(([k, rotulo]) => (
          <div
            key={k}
            className={`enc-kpi${(k === 'atrasadas' || k === 'comProblema') && p.indicadores[k] > 0 ? ' enc-kpi-alerta' : ''}`}
          >
            <span>{rotulo}</span>
            <b>{String(p.indicadores[k])}</b>
          </div>
        ))}
      </div>
      {p.atencao.length > 0 && (
        <section className="enc-atencao" aria-label="Precisa da sua atenção">
          <h2>Precisa da sua atenção</h2>
          <ul>
            {p.atencao.map((a) => (
              <li key={a.chave}>
                <button
                  type="button"
                  className={`enc-atencao-item enc-atencao-${a.tom}`}
                  onClick={() => aoFiltrar(a.filtro as FiltroDaLista)}
                >
                  <Icone nome={a.tom === 'problema' ? 'warn' : 'clock'} tamanho={16} traco={1.8} />
                  <span>{a.frase}</span>
                  <Icone nome="arrow" tamanho={14} traco={1.8} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Vínculos para revisar
 * ------------------------------------------------------------------ */

export function VinculosParaRevisar({ sessao }: { sessao: Sessao }) {
  const [lista, setLista] = useState<readonly Revisao[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [rodando, setRodando] = useState(false);
  const carregar = useCallback(
    () => void lerRevisoes().then((r) => r.ok && setLista(r.dados.revisoes)),
    [],
  );
  useEffect(carregar, [carregar]);
  const decidirAgora = async (r: Revisao, d: 'CONFIRMADO' | 'DESCARTADO') => {
    const x = await decidir(sessao, r.id, { decisao: d });
    setMsg(
      x.ok
        ? d === 'CONFIRMADO'
          ? 'Vínculo confirmado.'
          : 'Sugestão descartada (não volta a aparecer).'
        : x.falha.tipo === 'invalido' || x.falha.tipo === 'conflito'
          ? x.falha.mensagem
          : 'Não foi possível salvar.',
    );
    carregar();
  };
  const sincronizar = async () => {
    setRodando(true);
    const r = await sincronizarAgora(sessao);
    setRodando(false);
    setMsg(
      r.ok
        ? `Busca feita: ${String(r.dados.vinculos)} vínculo(s) novo(s), ${String(r.dados.revisar)} para revisar${r.dados.erros.length ? ` · ${String(r.dados.erros.length)} aviso(s) das fontes` : ''}.`
        : 'Não foi possível buscar agora.',
    );
    carregar();
  };
  const c = (r: Revisao, k: string) => {
    const v = r.candidato[k];
    return typeof v === 'string' || typeof v === 'number' ? String(v) : null;
  };
  return (
    <div className="enc-lista">
      <div className="enc-lista-barra">
        <p className="enc-dica">
          Vínculo automático só com pedido + produto + fornecedor. O resto espera uma pessoa aqui.
        </p>
        <button
          type="button"
          className="enc-botao-sec"
          onClick={() => void sincronizar()}
          disabled={rodando}
        >
          <Icone nome="retry" tamanho={14} traco={1.8} />{' '}
          {rodando ? 'Buscando…' : 'Buscar pedidos e notas agora'}
        </button>
      </div>
      {msg && <p className="enc-faixa-ok">{msg}</p>}
      {lista === null ? (
        <div className="enc-lista-esq enc-esqueleto">
          <i />
          <i />
        </div>
      ) : lista.length === 0 ? (
        <div className="enc-nada">
          <b>Nada para revisar.</b>
          <span>Os vínculos ambíguos ou prováveis aparecem aqui.</span>
        </div>
      ) : (
        <ul className="enc-cartoes">
          {lista.map((r) => (
            <li key={r.id}>
              <article className="enc-cartao enc-revisao">
                <div className="enc-cartao-corpo">
                  <div className="enc-cartao-topo">
                    <span className="enc-codigo">{r.item.codigo}</span>
                    <span className="enc-tag enc-tag-atencao">
                      {ROTULO_RAZAO[r.razao] ?? r.razao}
                    </span>
                    <span className="enc-tag">
                      {r.tipo === 'PEDIDO' ? 'Pedido' : 'Nota fiscal'} · nota {String(r.score)}
                    </span>
                  </div>
                  <h3 className="enc-cartao-nome">
                    {r.item.descricao} — falta {String(r.item.faltando)} de{' '}
                    {String(r.item.quantidade)}
                  </h3>
                  <p className="enc-cartao-sub">
                    Cliente {r.item.cliente} · sugerido {r.item.fornecedorConsultado ?? '—'}
                  </p>
                  <p className="enc-cartao-sub">
                    {r.tipo === 'PEDIDO'
                      ? `Pedido #${c(r, 'pedido') ?? '—'} · ${c(r, 'fornecedor') ?? '—'} · ${c(r, 'quantidade') ?? '—'} un · ${c(r, 'comprador') ?? ''}`
                      : `NF ${c(r, 'nfNumero') ?? '—'} · ${c(r, 'fornecedor') ?? '—'} · ${c(r, 'qtdNaLinha') ?? '—'} un na linha${c(r, 'pedido') ? ` · pedido ${c(r, 'pedido') ?? ''}` : ''}`}
                  </p>
                  <p className="enc-motivos">{r.motivos.join(' · ')}</p>
                </div>
                <div className="enc-revisao-acoes">
                  <button
                    type="button"
                    className="enc-botao"
                    onClick={() => void decidirAgora(r, 'CONFIRMADO')}
                  >
                    Confirmar vínculo
                  </button>
                  <button
                    type="button"
                    className="enc-botao-sec"
                    onClick={() => void decidirAgora(r, 'DESCARTADO')}
                  >
                    Não é desta encomenda
                  </button>
                </div>
              </article>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Configurações
 * ------------------------------------------------------------------ */

export function ConfiguracoesDeEncomendas({ sessao }: { sessao: Sessao }) {
  const [cfg, setCfg] = useState<ConfigDasEncomendas | null>(null);
  const [integ, setInteg] = useState<Integracoes | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    void lerConfig().then((r) => r.ok && setCfg(r.dados.config));
    void lerIntegracoes().then((r) => r.ok && setInteg(r.dados));
  }, []);
  const salvar = async (chave: keyof ConfigDasEncomendas, valor: unknown) => {
    const r = await salvarConfig(sessao, chave, valor);
    if (r.ok) {
      setCfg(r.dados.config);
      setMsg('Salvo (fica na trilha de auditoria).');
    } else
      setMsg(
        r.falha.tipo === 'invalido'
          ? r.falha.mensagem || 'Valor inválido.'
          : 'Não foi possível salvar.',
      );
  };
  if (!cfg)
    return (
      <div className="enc-lista-esq enc-esqueleto">
        <i />
        <i />
      </div>
    );
  const num = (v: string) => Math.max(0, Math.round(Number(v.replace(',', '.')) || 0));
  return (
    <div className="enc-config">
      {msg && <p className="enc-faixa-ok">{msg}</p>}

      <section className="enc-col">
        <header className="enc-col-cabeca">
          <h2>Prazos de atraso (SLA)</h2>
        </header>
        <div className="enc-col-corpo">
          <p className="enc-dica">
            Tempo parado na mesma situação. Mercadoria em transporte tem 3× o prazo. Prazo prometido
            vencido é sempre atraso.
          </p>
          <FormSla cfg={cfg} aoSalvar={(v) => void salvar('sla', v)} num={num} />
        </div>
      </section>

      <section className="enc-col">
        <header className="enc-col-cabeca">
          <h2>Consulta e robô de vínculo</h2>
        </header>
        <div className="enc-col-corpo enc-config-linhas">
          <LinhaNumero
            rotulo="Disponibilidade fica “antiga” depois de (min)"
            valor={cfg.disponibilidade.velhaDepoisMin}
            aoSalvar={(v) =>
              void salvar('disponibilidade', { ...cfg.disponibilidade, velhaDepoisMin: v })
            }
          />
          <LinhaNumero
            rotulo="Guardar a consulta por (segundos)"
            valor={cfg.disponibilidade.cacheSegundos}
            aoSalvar={(v) =>
              void salvar('disponibilidade', { ...cfg.disponibilidade, cacheSegundos: v })
            }
          />
          <LinhaInterruptor
            rotulo="Robô de vínculo ligado (pedido → NF → conferência)"
            valor={cfg.sincronizacao.ligada}
            aoMudar={(v) => void salvar('sincronizacao', { ...cfg.sincronizacao, ligada: v })}
          />
          <LinhaNumero
            rotulo="Rodar a cada (min)"
            valor={cfg.sincronizacao.minutos}
            aoSalvar={(v) => void salvar('sincronizacao', { ...cfg.sincronizacao, minutos: v })}
          />
        </div>
      </section>

      <section className="enc-col">
        <header className="enc-col-cabeca">
          <h2>Regras e exibição</h2>
        </header>
        <div className="enc-col-corpo enc-config-linhas">
          <LinhaInterruptor
            rotulo="Controlado exige informar a receita"
            valor={cfg.regras.receitaObrigatoriaNoControlado}
            aoMudar={(v) =>
              void salvar('regras', { ...cfg.regras, receitaObrigatoriaNoControlado: v })
            }
          />
          <LinhaInterruptor
            rotulo="Prazo prometido obrigatório"
            valor={cfg.regras.prazoObrigatorio}
            aoMudar={(v) => void salvar('regras', { ...cfg.regras, prazoObrigatorio: v })}
          />
          <LinhaInterruptor
            rotulo="Mostrar PMC na consulta"
            valor={cfg.exibicao.mostrarPmc}
            aoMudar={(v) => void salvar('exibicao', { mostrarPmc: v })}
          />
          <p className="enc-dica">
            Quem vê valor: nas encomendas, os donos e os administradores do módulo veem custo e
            margem; o balconista vê só PMC e preço de venda. Nas notas, o total, os boletos e o
            custo de cada item são dos administradores (o receptor vê nota e quantidades). Vendas e
            margem da loja continuam só com Nicolas e Carlos. Não é configurável aqui.
          </p>
        </div>
      </section>

      <section className="enc-col">
        <header className="enc-col-cabeca">
          <h2>Integrações</h2>
        </header>
        <div className="enc-col-corpo">
          {!integ ? (
            <div className="enc-lista-esq enc-esqueleto">
              <i />
              <i />
            </div>
          ) : (
            <>
              <ul className="enc-linhas">
                {integ.fontes.map((f) => (
                  <li key={f.nome} className="enc-linha">
                    <div className="enc-linha-topo">
                      <b>{f.nome}</b>
                      <span
                        className={`enc-status ${f.ok ? 'enc-status-verde' : 'enc-status-vermelho'}`}
                      >
                        <i />
                        {f.ok ? 'Conectado' : 'Fora'} · {String(f.latenciaMs)} ms
                      </span>
                    </div>
                    <div className="enc-linha-detalhe">
                      {f.uso}
                      {f.mensagem ? ` — ${f.mensagem}` : ''}
                    </div>
                  </li>
                ))}
              </ul>
              <h3 className="enc-subtitulo">Últimas buscas do robô</h3>
              <ul className="enc-linhas">
                {integ.execucoes.length === 0 && (
                  <li className="enc-col-vazio">Ainda não rodou.</li>
                )}
                {integ.execucoes.map((e) => (
                  <li key={e.iniciadaEm} className="enc-linha">
                    <div className="enc-linha-topo">
                      <b>
                        {dataHoraBr(e.iniciadaEm)} ·{' '}
                        {e.origem === 'MANUAL' ? 'manual' : 'automática'}
                      </b>
                      <span>
                        {String(e.vinculos)} vínculo(s) · {String(e.revisar)} p/ revisar
                      </span>
                    </div>
                    {e.erros.length > 0 && (
                      <div className="enc-linha-detalhe enc-n-zero">
                        {e.erros.slice(0, 3).join(' · ')}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>

      <section className="enc-col">
        <header className="enc-col-cabeca">
          <h2>Distribuidoras na consulta</h2>
        </header>
        <div className="enc-col-corpo">
          {!integ ? null : integ.distribuidoras.length === 0 ? (
            <span className="enc-col-vazio">
              O InovaPED não respondeu a lista de distribuidoras.
            </span>
          ) : (
            <ul className="enc-linhas">
              {integ.distribuidoras.map((d) => {
                const k = d.slug ?? d.nome;
                const aj = cfg.distribuidoras[k] ?? { ativa: true, nome: null };
                return (
                  <li key={k} className="enc-linha enc-distribuidora">
                    <div className="enc-linha-topo">
                      <b>{d.nome}</b>
                      <span
                        className={`enc-status enc-status-${d.estado === 'ok' ? 'verde' : d.estado === 'atrasada' ? 'ambar' : 'vermelho'}`}
                      >
                        <i />
                        {d.estado === 'ok'
                          ? 'Em dia'
                          : d.estado === 'atrasada'
                            ? 'Atrasada'
                            : d.estado === 'parada'
                              ? 'Parada'
                              : 'Nunca carregou'}
                        {d.horas !== null ? ` · ${String(Math.round(d.horas))} h` : ''}
                      </span>
                    </div>
                    <div className="enc-linha-detalhe">
                      <label className="enc-urgente">
                        <input
                          type="checkbox"
                          checked={aj.ativa}
                          onChange={(e) =>
                            void salvar('distribuidoras', {
                              ...cfg.distribuidoras,
                              [k]: { ...aj, ativa: e.target.checked },
                            })
                          }
                        />{' '}
                        Aparece na consulta
                      </label>
                      <NomeExibido
                        valor={aj.nome ?? ''}
                        aoSalvar={(nome) =>
                          void salvar('distribuidoras', {
                            ...cfg.distribuidoras,
                            [k]: { ...aj, nome: nome || null },
                          })
                        }
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <ConfiguracoesDasFaltas sessao={sessao} cfg={cfg} salvar={salvar} />
    </div>
  );
}

function FormSla({
  cfg,
  aoSalvar,
  num,
}: {
  cfg: ConfigDasEncomendas;
  aoSalvar: (v: ConfigDasEncomendas['sla']) => void;
  num: (v: string) => number;
}) {
  const [v, setV] = useState({
    na: String(cfg.sla.normal.atencaoMin / 60),
    nt: String(cfg.sla.normal.atrasoMin / 60),
    ua: String(cfg.sla.urgente.atencaoMin),
    ut: String(cfg.sla.urgente.atrasoMin),
  });
  return (
    <div className="enc-grade-form">
      <label className="enc-campo">
        <span className="enc-campo-rotulo">Normal · atenção (h)</span>
        <input
          value={v.na}
          onChange={(e) => setV({ ...v, na: e.target.value })}
          inputMode="decimal"
        />
      </label>
      <label className="enc-campo">
        <span className="enc-campo-rotulo">Normal · atrasado (h)</span>
        <input
          value={v.nt}
          onChange={(e) => setV({ ...v, nt: e.target.value })}
          inputMode="decimal"
        />
      </label>
      <label className="enc-campo">
        <span className="enc-campo-rotulo">Urgente · atenção (min)</span>
        <input
          value={v.ua}
          onChange={(e) => setV({ ...v, ua: e.target.value })}
          inputMode="numeric"
        />
      </label>
      <label className="enc-campo">
        <span className="enc-campo-rotulo">Urgente · atrasado (min)</span>
        <input
          value={v.ut}
          onChange={(e) => setV({ ...v, ut: e.target.value })}
          inputMode="numeric"
        />
      </label>
      <button
        type="button"
        className="enc-botao"
        onClick={() =>
          aoSalvar({
            normal: {
              atencaoMin: Math.round(Number(v.na.replace(',', '.')) * 60),
              atrasoMin: Math.round(Number(v.nt.replace(',', '.')) * 60),
            },
            urgente: { atencaoMin: num(v.ua), atrasoMin: num(v.ut) },
          })
        }
      >
        Salvar prazos
      </button>
    </div>
  );
}

function LinhaNumero({
  rotulo,
  valor,
  aoSalvar,
}: {
  rotulo: string;
  valor: number;
  aoSalvar: (v: number) => void;
}) {
  const [v, setV] = useState(String(valor));
  useEffect(() => setV(String(valor)), [valor]);
  return (
    <div className="enc-config-linha">
      <span>{rotulo}</span>
      <input
        value={v}
        onChange={(e) => setV(e.target.value.replace(/\D/g, ''))}
        inputMode="numeric"
        aria-label={rotulo}
      />
      <button
        type="button"
        className="enc-botao-sec"
        disabled={v === String(valor)}
        onClick={() => aoSalvar(Number(v))}
      >
        Salvar
      </button>
    </div>
  );
}

function LinhaInterruptor({
  rotulo,
  valor,
  aoMudar,
}: {
  rotulo: string;
  valor: boolean;
  aoMudar: (v: boolean) => void;
}) {
  return (
    <label className="enc-config-linha">
      <span>{rotulo}</span>
      <input
        type="checkbox"
        role="switch"
        checked={valor}
        onChange={(e) => aoMudar(e.target.checked)}
        className="enc-interruptor"
      />
    </label>
  );
}

function NomeExibido({ valor, aoSalvar }: { valor: string; aoSalvar: (v: string) => void }) {
  const [v, setV] = useState(valor);
  return (
    <span className="enc-nome-exibido">
      <input
        value={v}
        onChange={(e) => setV(e.target.value)}
        placeholder="Nome exibido (opcional)"
        aria-label="Nome exibido"
        maxLength={80}
      />
      {v !== valor && (
        <button type="button" className="enc-link" onClick={() => aoSalvar(v.trim())}>
          Salvar
        </button>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Auditoria
 * ------------------------------------------------------------------ */

const ROTULO_DA_ACAO: Readonly<Record<string, string>> = {
  'encomenda.criada': 'Encomenda criada',
  'encomenda.pedido.vinculado': 'Pedido vinculado',
  'encomenda.pedido.desvinculado': 'Pedido desvinculado',
  'encomenda.nf.vinculada': 'NF vinculada',
  'encomenda.nf.desvinculada': 'NF desvinculada',
  'encomenda.vinculo.descartado': 'Sugestão descartada',
  'encomenda.recebimento': 'Chegada confirmada',
  'encomenda.conferencia': 'Conferência',
  'encomenda.divergencia.resolvida': 'Divergência resolvida',
  'encomenda.pagamento': 'Pagamento',
  'encomenda.resolvida': 'Encomenda resolvida',
  'encomenda.config': 'Configuração alterada',
};

export function AuditoriaDeEncomendas() {
  const [eventos, setEventos] = useState<Awaited<ReturnType<typeof lerAuditoria>> | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);
  useEffect(() => void lerAuditoria().then(setEventos), []);
  if (!eventos)
    return (
      <div className="enc-lista-esq enc-esqueleto">
        <i />
        <i />
      </div>
    );
  if (!eventos.ok) return <p className="enc-erro">Não foi possível carregar a auditoria.</p>;
  return (
    <section className="enc-col">
      <header className="enc-col-cabeca">
        <h2>Auditoria das encomendas</h2>
        <span className="enc-col-fonte">
          As 200 ações mais recentes · quem, quando, antes e depois
        </span>
      </header>
      <div className="enc-col-corpo">
        <ul className="enc-linhas">
          {eventos.dados.eventos.map((e) => (
            <li key={e.id} className="enc-linha">
              <button
                type="button"
                className="enc-linha-botao"
                onClick={() => setAberto(aberto === e.id ? null : e.id)}
                aria-expanded={aberto === e.id}
              >
                <div className="enc-linha-topo">
                  <b>
                    {ROTULO_DA_ACAO[e.tipo] ?? e.tipo}
                    {e.codigo ? ` · ${e.codigo}` : ''}
                  </b>
                  <span className="enc-linha-data">{dataHoraBr(e.em)}</span>
                </div>
                <div className="enc-linha-detalhe">
                  por {e.por}
                  {e.origem !== 'usuario' ? ' (automático)' : ''}
                </div>
              </button>
              {aberto === e.id && (
                <pre className="enc-json">{JSON.stringify(e.metadados, null, 2)}</pre>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
